import { modalActions } from '@/actions';
import { LinkButton } from '@/components/elements/forms/buttons';
import Logotype from '@/components/elements/Logotype';
import NavLink from '@/components/elements/NavLink';
import { NAVIGATION_TRIGGER } from '@/constants/navigation';
import { classnames } from '@/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { LoginContext } from '@/hooks/useLogin';
import { _t } from '@/locale/i18n';
import { memo, MouseEvent, ReactNode, useContext } from 'react';
import LanguageSwitcher from './components/LanguageSwitcher';
import SocialButton from './components/SocialButton';
import { SOCIAL_BUTTON } from './components/SocialButton.constants';
import { baseTranslationKey, FOOTER_LINKS, WHITE_LABEL_FOOTER_LINKS } from './Footer.constants';
import { Identifier, LinkGroup } from './Footer.types';

const externalAttrs = (external?: boolean) =>
  external ? { target: '_blank' as const, rel: 'noopener noreferrer' } : ({} as const);

const CURRENT_YEAR = new Date().getFullYear();

/**
 * Subcomponents for Footer
 */

const AppDownloadSection = () => {
  return (
    <section id="app-download" className="gap-y-lg flex flex-1 flex-col" aria-labelledby="app-download-heading">
      <h2 id="app-download-heading" className="text-h-s text-fg_primary w-full">
        {_t(`${baseTranslationKey}.section.app-download.heading`)}
      </h2>
      <div className="gap-md flex">
        <a
          href="https://bokadirekt.smart.link/cl1p2rx9j"
          className="focus:ring-border_focus rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
          {...externalAttrs(true)}>
          <img
            loading="lazy"
            decoding="async"
            src="/images/download-app-store.svg"
            className="h-[40px]"
            alt={_t('availableAppStore')}
          />
        </a>
        <a
          href="https://bokadirekt.smart.link/cl1p2rx9j"
          className="focus:ring-border_focus rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
          {...externalAttrs(true)}>
          <img
            loading="lazy"
            decoding="async"
            src="/images/download-google-play.svg"
            className="h-[40px]"
            alt={_t('availablePlayStore')}
          />
        </a>
      </div>
    </section>
  );
};

const MerchantSection = () => {
  return (
    <section id="merchant" className="gap-y-lg flex flex-1 flex-col" aria-labelledby="merchant-heading">
      <h2 id="merchant-heading" className="text-h-s text-fg_primary">
        {_t(`${baseTranslationKey}.section.merchant.heading`)}
      </h2>
      <div className="space-x-lg flex items-center">
        <LinkButton
          {...externalAttrs(true)}
          variant="primary"
          size="md"
          className="flex-shrink-0"
          to="https://business.bokadirekt.se/"
          aria-label={_t(`${baseTranslationKey}.section.merchant.cta-readmore.aria-label`)}>
          {_t(`${baseTranslationKey}.section.merchant.cta-readmore.label`)}
        </LinkButton>
        <NavLink
          className="w-full lg:w-auto"
          to="https://portal.bokadirekt.se/"
          aria-label={_t(`${baseTranslationKey}.section.merchant.cta-login.aria-label`)}
          {...externalAttrs(true)}>
          {_t(`${baseTranslationKey}.section.merchant.cta-login.label`)}
        </NavLink>
      </div>
    </section>
  );
};

const LinkGroups = () => {
  const loginContext = useContext(LoginContext);
  const dispatch = useAppDispatch();
  const isWhitelabel = useAppSelector((state) => state?.settings?.isWhitelabel);

  const handleLinkClick = (event: MouseEvent<HTMLElement>, identifier: Identifier) => {
    event.currentTarget?.blur?.();

    switch (identifier) {
      case Identifier.Login:
        loginContext.show ? loginContext.onClose() : loginContext.onOpen();
        dispatch(
          modalActions.login({
            show: false,
            triggerSource: NAVIGATION_TRIGGER.FOOTER,
          }),
        );
        return;
    }
  };

  return (
    <>
      {Object.entries(!isWhitelabel ? FOOTER_LINKS : WHITE_LABEL_FOOTER_LINKS).map(([key, links]) => {
        const group = key as LinkGroup;
        const heading = _t(`${baseTranslationKey}.group-heading.${group}`);
        const headingId = `${group}-heading`;

        return (
          <section key={key} className="space-y-sm" aria-labelledby={headingId}>
            <h2 id={headingId} className="text-h-s text-fg_primary whitespace-nowrap">
              {heading}
            </h2>
            <nav
              className="flex flex-col items-start"
              aria-label={_t(`${baseTranslationKey}.section.link-group.aria-label`, { group: heading })}>
              <ul className="flex flex-col items-start">
                {links.map((link) => {
                  if (link.identifier === Identifier.Login && loginContext.user) {
                    return null;
                  }

                  return (
                    <li key={link.identifier}>
                      <NavLink
                        className="whitespace-nowrap"
                        to={link.to}
                        aria-label={link.external ? `${link.label} (${_t('opensInNewTab')})` : link.label}
                        onClick={(e) => handleLinkClick(e, link.identifier)}
                        {...externalAttrs(!!link.external)}>
                        {link.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </section>
        );
      })}
    </>
  );
};

const MainContent = () => {
  const isWhitelabel = useAppSelector((state) => state?.settings?.isWhitelabel);

  const layoutClasses = classnames(
    `grid grid-cols-1 grid-rows-[auto_auto] gap-3xl pb-2xl`,
    ` @sm:grid-cols-3`,
    ` @lg:grid-cols-[auto_auto_auto_auto] items-baseline`,
  );

  return (
    <div className={classnames(layoutClasses, 'border-border_regular border-b')}>
      <div
        className={classnames(
          'gap-3xl border-border_regular pb-3xl inline-flex flex-shrink-0 flex-col border-b',
          '@lg:border-none @lg:pb-0 @sm:flex-row @lg:flex-col',
          '@sm:col-span-4 @lg:col-span-1',
        )}>
        {!isWhitelabel && <AppDownloadSection />}
        <MerchantSection />
      </div>

      <LinkGroups />
    </div>
  );
};

const Container = ({ children }: { children: ReactNode }) => (
  <div className="@container px-xl py-xl pb-2xl space-y-3xl md:p-3xl mt-md bg-surface_l2 rounded-md drop-shadow-lg">
    {children}
  </div>
);

const SocialButtons = memo(function SocialButtons() {
  return (
    <nav aria-label="Sociala medier">
      <ul className="gap-xs md:gap-x-xl flex list-none">
        {Object.entries(SOCIAL_BUTTON).map(([key, { to, icon, alt }]) => (
          <li key={key}>
            <SocialButton icon={icon} alt={alt} to={to} />
          </li>
        ))}
      </ul>
    </nav>
  );
});

const BottomContent = () => (
  <div className="flex items-center justify-between">
    <SocialButtons />
    <LanguageSwitcher />
  </div>
);

const CopyrightNotice = () => (
  <div className="py-xl mx-sm flex items-center justify-between">
    <Logotype variant="logo" />
    <div className="text-l text-fg_secondary flex-shrink-0">&copy; Bokadirekt {CURRENT_YEAR}</div>
  </div>
);

interface FooterCompound extends React.FC<{ children: ReactNode }> {
  Container: typeof Container;
  MainContent: typeof MainContent;
  BottomContent: typeof BottomContent;
  CopyrightNotice: typeof CopyrightNotice;
}

const FooterBase = ({ children }: { children: ReactNode }) => <footer className="flex flex-col">{children}</footer>;

const Footer = FooterBase as FooterCompound;

Footer.Container = Container;
Footer.MainContent = MainContent;
Footer.BottomContent = BottomContent;
Footer.CopyrightNotice = CopyrightNotice;

export default function FooterRoot() {
  return (
    <Footer>
      <Footer.Container>
        <Footer.MainContent />
        <Footer.BottomContent />
      </Footer.Container>
      <Footer.CopyrightNotice />
    </Footer>
  );
}
